import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  faTriangleExclamation,
  faArrowRotateRight,
  faTrashCan,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactConfirmationDialog from 'shared/design-system/components/TrueImpactConfirmationDialog';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { requestInit } from 'shared/auth/hooks/useRequestState';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { feedbackSectionIdToString } from '../../../NPOFeedback/utils';
import {
  deleteSectionAnswers,
  DeleteSectionAnswersOptions,
  DeleteSectionAnswersResponse,
} from './api';

const handleRefresh = () => {
  window.location.reload();
};

interface SectionLoadErrorProps {
  reportId: number | string;
  sectionId: FeedbackSectionId;
  onResetSuccess?: () => unknown;
  deleteOptions?: DeleteSectionAnswersOptions;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACERS.small};
  padding: ${SPACERS.large};
  margin: ${SPACERS.medium} auto;
  max-width: 560px;
  border: 1px solid ${COLORS.criticalSubdued};
  border-radius: 8px;
`;

const IconWrap = styled.div`
  font-size: 32px;
  color: ${COLORS.critical};
`;

const Actions = styled.div`
  display: flex;
  gap: ${SPACERS.small};
  margin-top: ${SPACERS.small};
  flex-wrap: wrap;
  justify-content: center;
`;

const SectionLoadError: React.FC<SectionLoadErrorProps> = ({
  reportId,
  sectionId,
  onResetSuccess,
  deleteOptions,
}) => {
  const client = useClient();
  const sectionName = feedbackSectionIdToString(sectionId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteState, makeDeleteRequest, setDeleteState] = useGraphQLRequest<
    DeleteSectionAnswersResponse
  >(undefined, true);

  const isDeleting = deleteState.type === 'REQUEST_START';
  const deleteError =
    deleteState.type === 'REQUEST_ERROR'
      ? deleteState.error.response?.data?.message ||
        'Unable to reset this section. Please try again.'
      : null;

  useEffect(() => {
    if (
      deleteState.type === 'REQUEST_SUCCESS' &&
      deleteState.data?.deleteSectionAnswers?.success
    ) {
      setDeleteState(requestInit());
      setConfirmOpen(false);
      onResetSuccess?.();
      handleRefresh();
    }
  }, [deleteState, onResetSuccess, setDeleteState]);

  const handleOpenConfirm = useCallback(() => setConfirmOpen(true), []);
  const handleCloseConfirm = useCallback(() => {
    if (isDeleting) return;
    setDeleteState(requestInit());
    setConfirmOpen(false);
  }, [isDeleting, setDeleteState]);

  const handleConfirmReset = useCallback(() => {
    if (!client) return;
    makeDeleteRequest(
      deleteSectionAnswers(client, Number(reportId), sectionId, deleteOptions),
    );
  }, [client, makeDeleteRequest, reportId, sectionId, deleteOptions]);

  return (
    <>
      <Container>
        <IconWrap>
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </IconWrap>
        <TrueImpactText variant="heading-subheading" align="center">
          Unable to load this question
        </TrueImpactText>
        <TrueImpactText align="center">
          Something went wrong while loading this question. Please try
          refreshing the page. If the error persists, you can start the{' '}
          <strong>{sectionName}</strong> section from scratch — this will remove
          your existing answers for this section so you can start over.
        </TrueImpactText>
        <Actions>
          <TrueImpactButton
            variant="primary-large"
            backgroundColor="critical"
            backgroundColorHover="criticalHover"
            onClick={handleOpenConfirm}
          >
            <FontAwesomeIcon icon={faTrashCan} />
            Restart {sectionName} section
          </TrueImpactButton>
          <TrueImpactButton onClick={handleRefresh}>
            <FontAwesomeIcon icon={faArrowRotateRight} />
            Refresh page
          </TrueImpactButton>
        </Actions>
      </Container>
      <TrueImpactConfirmationDialog
        open={confirmOpen}
        icon={faTriangleExclamation}
        iconColor="critical"
        handleClose={handleCloseConfirm}
        next={{
          backgroundColor: 'critical',
          backgroundColorHover: 'criticalHover',
          text: `Start ${sectionName} over`,
          isLoading: isDeleting,
          disabled: isDeleting,
          onClick: handleConfirmReset,
        }}
        previous={{
          text: 'Cancel',
          onClick: handleCloseConfirm,
          disabled: isDeleting,
        }}
      >
        <TrueImpactText align="center">
          Are you sure you want to start the <strong>{sectionName}</strong>{' '}
          section over? Your existing answers for this section will be removed
          and you will start from scratch.
        </TrueImpactText>
        {deleteError && (
          <TrueImpactText
            align="center"
            variant="body-small"
            style={{ color: COLORS.critical, marginTop: SPACERS.small }}
          >
            {deleteError}
          </TrueImpactText>
        )}
      </TrueImpactConfirmationDialog>
    </>
  );
};

export default SectionLoadError;
